import {type FC, useCallback, useEffect, useRef} from 'react';
import LeftMenu from '../layouts/menu/LeftMenu.tsx';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router';
import StreamingPage from './stream/StreamingPage.tsx';
import ReplayPage from './replay/ReplayPage.tsx';
import IpTvPage from './ipTV/IpTvPage.tsx';
import styled from 'styled-components';
import useCenterStore from '../store/useCenterStore.ts';
import * as React from 'react';
import MainLayout from '../layouts/MainLayout.tsx';
import useStreamingStore from '../store/useStreamingStore.ts';
import {useQuery} from '@tanstack/react-query';
import callApi from '../apis/callApi.ts';
import {readRustDeskId} from '../apis/rustdeskId.ts';
import useModal from '../hooks/useModal.tsx';
import ModalRenderer from '../compoents/modal/ModalRenderer.tsx';
import type {PopupType} from '../compoents/modal';
import useGlobalStore from '../store/useGlobalStore.ts';
import DeviceIDError from './error/DeviceIDError.tsx';
import menuData from '../layouts/menu/types/menuData.json';
import BroadcastPage from './broadcast/BroadcastPage.tsx';
import type {CenterInfo} from '../models';
import Watching from "./mornitoring/Watching.tsx";
import Package from '../../package.json';

const MainContent = styled.div`
    height: 100%;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1.5rem;
    background-color: #1c1c1c;
    border-radius: 1.5rem;
    overflow: hidden;
`;

const isPopupType = (value: any): value is PopupType => {
    return ['message', 'notice', 'video_call'].includes(value);
};

const FEATURE_MAP: Record<string, string> = {
    streaming: 'live_streaming',
    iptv: 'iptv',
    replay: 'vod',
};

const Main: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const searchParamDeviceId = searchParams.get('deviceID');
    const token = searchParams.get('token');
    const {
        centerInfo,
        deviceID,
        setDeviceID,
        setCenterInfo,
        electronVer,
        setElectronVer,
        getCenterInfo
    } = useCenterStore();

    const prevTimeRef = useRef<number>(Date.now());
    const prevMenuRef = useRef<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const ONE_HOUR = 60 * 60 * 1000;

    const fetchStatistics = useCallback(
        async (feature: string) => {
            const now = Date.now();
            const duration = Math.floor((now - prevTimeRef.current) / 1000);
            prevTimeRef.current = now;
            if (!centerInfo?.center_id) {
                return;
            }
            const rustdeskId = await readRustDeskId();
            const params: Record<string, unknown> = {
                center_code: centerInfo?.center_id,
                feature_code: feature,
                duration,
                grade: 'info',
                tv_client_ver: electronVer,
                web_client_ver: Package.version,
                device_id: deviceID,
                rustdesk_id: rustdeskId
            };
            const filteredEntries = Object.entries(params).filter(([_, v]) => {
                if (v === null || v === undefined) return false;
                if (typeof v === 'string' && v.trim() === '') return false;
                return true;
            });
            const qs = new URLSearchParams(
                filteredEntries.map(([k, v]) => [k, String(v)])
            ).toString();
            const url = `statistic/client/?${qs}`;
            await callApi().get(url);
        },
        [centerInfo?.center_id, electronVer, deviceID]
    );

    useEffect(() => {
        if (!deviceID) return;
        const startInterval = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            intervalRef.current = setInterval(() => {
                const currentMenu = location.pathname.replace('/', '');
                const feature = FEATURE_MAP[currentMenu];
                if (feature) {
                    fetchStatistics(feature);
                }
            }, ONE_HOUR);
        };
        startInterval();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [deviceID, location.pathname, fetchStatistics]);

    useEffect(() => {
        const currentMenu = location.pathname.replace('/', '');

        if (deviceID && prevMenuRef.current) {
            const feature = FEATURE_MAP[prevMenuRef.current];
            if (feature) fetchStatistics(feature);
        }

        prevMenuRef.current = currentMenu;
    }, [location.pathname, deviceID, fetchStatistics]);

    const fetchData = async () => {
        if (window.electronAPI) {
            const data = await window.electronAPI.getAppInfo();
            setElectronVer(data.version);
        } else {
            setElectronVer("");
        }
    };

    useEffect(() => {
        if (token) {
            try {
                const payload = token.split('.')[1];
                const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
                const decoded = JSON.parse(atob(base64));
                console.log('Decoded token:', decoded);

                localStorage.setItem('ACCESS_TOKEN', token);

                const centerData: CenterInfo = {
                    center_id: `MCH-${String(decoded.center_data.center_number).padStart(3, '0')}`,
                    id: decoded.center_data.id,
                    name: decoded.center_data.name,
                    address: decoded.center_data.address || undefined,
                    latitude: decoded.center_data.latitude,
                    longitude: decoded.center_data.longitude,
                    district: undefined,
                    health_care_device_id: undefined,
                    manager: null,
                    smart_table_id: undefined,
                    walking_machine_id: undefined,
                };

                setCenterInfo(centerData);
                setDeviceID(decoded.center_data.device_id);
            } catch (e) {
                console.error('Token 디코드 실패:', e);
            }
        }
    }, [token]);

    useEffect(() => {
        fetchData();
        if (isBroadcast || isWatching) {
            return;
        } else {
            if (!isWatching) {
                if (token) {
                    // token useEffect에서 처리
                    return;
                }
                if (searchParamDeviceId) {
                    setDeviceID(searchParamDeviceId);
                } else {
                    navigate('/error');
                }
            }
        }
    }, []);

    useEffect(() => {
        if (deviceID && !centerInfo) getCenterInfo(deviceID);
    }, [deviceID]);

    const {showPopup} = useModal();
    const {generateId} = useGlobalStore();
    const {streamingPollingData, setStreamingPollingData} = useStreamingStore();

    useQuery({
        queryKey: ['streamingPollingData', centerInfo?.center_id],
        queryFn: async () => {
            if (!centerInfo?.center_id) return null;
            const path = location.pathname.split('/').filter(Boolean)[0] || '';
            const {data} = await callApi().get(
                `/center/polling/?center_code=${centerInfo.center_id}&current_menu=${path}`
            );
            setStreamingPollingData(data);
            for (const props in data) {
                if (isPopupType(props) && data[props]) {
                    if (props === 'notice') {
                        // TEMP
                        if (localStorage.getItem('notice') === data[props].id + '') continue;
                        if (
                            streamingPollingData === null ||
                            (streamingPollingData && streamingPollingData[props].id !== data[props].id)
                        ) {
                            const id = generateId();
                            // 공지 제목을 '알림'으로 고정하면 어르신이 어떤 공지인지 알 수 없다.
                            // 실제 제목을 넘기고, 비어 있을 때의 '알림' 대체는 Popup이 담당한다.
                            showPopup('notice', {
                                id: id,
                                title: data[props].title,
                                content: data[props].content,
                            });
                            // TEMP
                            localStorage.setItem('notice', data[props].id);
                        }
                    } else {
                        continue;
                    }
                }
            }
            return data;
        },
        enabled: !!centerInfo?.center_id,
        staleTime: 1000 * 15,
        refetchInterval: 1000 * 15,
        refetchIntervalInBackground: false,
        retry: 1,
        retryDelay: 1000,
    });

    const validPaths = menuData.map((item) => `/${item.name}`);
    validPaths.push('/');
    const isValidPath = validPaths.includes(location.pathname);
    const isBroadcast = location.pathname === '/server';
    const isWatching = location.pathname === '/watching';
    const getPageComponent = (name: string) => {
        switch (name) {
            case 'streaming':
                return <StreamingPage/>;
            case 'iptv':
                return <IpTvPage/>;
            case 'replay':
                return <ReplayPage/>;
            default:
                return <DeviceIDError/>;
        }
    };

    const setCpuInfo = async () => {
        if (window.electronAPI) {
            const {cpuInfo} = await window.electronAPI.getCpuInfo();
            localStorage.setItem('cpu-info', cpuInfo);
        } else {
            localStorage.setItem('cpu-info', '');
        }
        // CPU와 무관하게 store 기본값('best-perform')을 그대로 쓴다.
    };

    useEffect(() => {
        setCpuInfo();
    }, []);

    return (
        <>
            {isValidPath ? (
                <MainLayout>
                    <LeftMenu/>
                    <MainContent>
                        <Routes>
                            <Route path="/" element={<Navigate to={`/streaming`} replace/>}/>
                            {menuData.map((item) => (
                                <Route key={item.id} path={`/${item.name}`} element={getPageComponent(item.name)}/>
                            ))}
                        </Routes>
                    </MainContent>
                    <ModalRenderer/>
                </MainLayout>
            ) : isBroadcast ? (
                <BroadcastPage/>
            ) : isWatching ? (
                <Watching/>
            ) : (
                <DeviceIDError/>
            )}
        </>
    );
};

export default Main;
